import type { IntlShape } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { testHasTextMatch } from '~/eds/src/utils/testHasTextMatch';
import { Filter } from '~/evifields/src/types/Filter';
import { api } from '~/redux/api/createApi';
import { mapScopeToFilters } from '~/redux/api/methods/sylvanus/transformers';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { LibraryModelVersion, Tag } from './types';
import { getLatestModelVersions } from './utils';

export const useGetModelLibrarySteps = (intl: IntlShape) => {
  const dispatch = useDispatch();
  const step = useSelector(slices.xRayLibrary.selectors.selectStep);

  return [
    {
      id: 'model-library',
      isCompleted: step !== 'model-library',
      label: intl.formatMessage({
        id: 'ModelLibrarySteps.selectFromModelLibrary',
        defaultMessage: 'Select from Model Library',
        description: 'Label for select from model library step',
      }),
      onSelectStep: () =>
        dispatch(slices.xRayLibrary.actions.setStep('model-library')),
    },
    {
      id: 'add-scope',
      label: intl.formatMessage({
        id: 'ModelLibrarySteps.addScope',
        defaultMessage: 'Add Scope',
        description: 'Label for add scope step',
      }),
      isCompleted: step === 'confirm-publish',
      onSelectStep: () =>
        dispatch(slices.xRayLibrary.actions.setStep('add-scope')),
    },
    {
      id: 'confirm-publish',
      label: intl.formatMessage({
        id: 'ModelLibrarySteps.confirmPublish',
        defaultMessage: 'Confirm & Publish',
        description: 'Label for confirm publish step',
      }),
      onSelectStep: () =>
        dispatch(slices.xRayLibrary.actions.setStep('confirm-publish')),
    },
  ];
};

export const useGetLatestLibraryModelVersions = ({
  category,
  filters,
  search,
}: {
  category?: Nullable<Tag>;
  filters?: Filter[];
  search?: Nullable<string>;
}): {
  allModels: LibraryModelVersion[];
  models: LibraryModelVersion[];
  isLoading: boolean;
} => {
  const { data: searchFilters, isFetching: isFetchingFilters } =
    api.endpoints.getFilters.useQuery(undefined);

  const { data: models = [], isLoading: isLoadingModels } =
    api.endpoints.getLibraryModels.useQuery({});

  const isLoading = isLoadingModels || isFetchingFilters;

  const latestModelVersions = getLatestModelVersions(models);

  const modelVersions = latestModelVersions
    .map((version) => ({
      ...version,
      scope:
        version.config.scope && searchFilters
          ? mapScopeToFilters(version.config.scope, searchFilters)
          : [],
    }))
    .filter(testModelVersionMatchesCategory(category));

  return {
    allModels: modelVersions,
    models: getFilteredAndSearchedModels(modelVersions, search, filters),
    isLoading,
  };
};

export const useGetLibraryModelVersion = ({
  libraryModelId,
  libraryModelVersion,
}: {
  libraryModelId?: Uuid;
  libraryModelVersion?: string; // e.g. "1.0.0"
}): { isLoading: boolean; model?: LibraryModelVersion } => {
  const { data: versions = [], isFetching: isLoading } =
    api.endpoints.getLibraryModel.useQuery(
      {
        libraryModelId,
      },
      {
        skip: !libraryModelId || !libraryModelVersion,
      },
    );
  const model = versions.find(
    (version: LibraryModelVersion) => version.version === libraryModelVersion,
  );
  return { model, isLoading };
};

export const useGetLibraryModelVersions = ({
  libraryModelId,
}: {
  libraryModelId: Uuid;
}): { versions: LibraryModelVersion[]; isLoading: boolean } => {
  const { data: versions = [], isFetching: isLoading } =
    api.endpoints.getLibraryModel.useQuery({
      libraryModelId,
    });
  return { versions, isLoading };
};

const getFilteredAndSearchedModels = (
  models: LibraryModelVersion[],
  search?: Nullable<string>,
  filters?: Filter[],
) => {
  const searchedModels = search ? filterBySearch(models, search) : models;
  return filters ? filterByFilters(searchedModels, filters) : searchedModels;
};

const filterBySearch = (models: LibraryModelVersion[], search: string) =>
  models.filter(
    (version) =>
      testHasTextMatch(version.label, search) ||
      testHasTextMatch(version.description, search),
  );

const filterByFilters = (models: LibraryModelVersion[], filters: Filter[]) => {
  // Possibly extend this if more filters are added.
  const publishStatusFilter = filters.find(
    (filter) => filter.fieldId === 'modelState',
  );
  // no filter selected means show all results
  return publishStatusFilter && publishStatusFilter.values.length
    ? models.filter((version) =>
        publishStatusFilter.values.includes(version.promptModelPublishStatus),
      )
    : models;
};

const testModelVersionMatchesCategory =
  (category?: Nullable<Tag>) => (version: LibraryModelVersion) => {
    if (category) {
      return category.label === 'Uncategorized'
        ? !version.tags?.length
        : Boolean(version.tags?.find((tag) => tag.label === category.label));
    }
    return true;
  };
